import { Antriebsart, FahrzeugKlasse, MVSErgebnis, MVSParams, Zahlungsintervall } from '../types/mvs'

/**
 * Klasse zur Berechnung der Motorbezogenen Versicherungssteuer in Österreich
 * gemäß § 6 des Versicherungssteuergesetzes.
 */

/**
 * Berechnet die motorbezogene Versicherungssteuer gemäß dem angegebenen Zahlungsintervall.
 * Berücksichtigt automatisch die Befreiungen gemäß § 4 Abs. 3 Z 6 und 7.
 * 
 * @param params Parameter für die MVS-Berechnung
 * @returns Das Ergebnis der MVS-Berechnung
 */
export function berechneNeueSteuer(params: MVSParams): MVSErgebnis {
  const {
    fahrzeugKlasse,
    antriebsart,
    erstzulassungsDatum,
    motorLeistungKW,
    hubraumCCM,
    co2EmissionGKM,
    eigengewichtKG,
    zahlungsintervall,
    istFremdzuendungsMotorVor1987,
    haeltSchadstoffgrenzwerteEin
  } = params

  // Befreiung für elektrisch angetriebene Krafträder mit Leistung bis 4 kW (§ 4 Abs. 3 Z 6)
  if (fahrzeugKlasse === FahrzeugKlasse.L1E_BIS_L5E && 
      antriebsart === Antriebsart.ELEKTRISCH && 
      motorLeistungKW <= 4) {
    return {
      monatlicheSteuer: 0,
      steuerProIntervall: 0,
      steuerJaehrlich: 0
    }
  }
  
  // Befreiung für Krafträder mit Hubraum bis 100 ccm (§ 4 Abs. 3 Z 7)
  if (fahrzeugKlasse === FahrzeugKlasse.L1E_BIS_L5E && 
      hubraumCCM !== undefined && 
      hubraumCCM <= 100) {
    return {
      monatlicheSteuer: 0,
      steuerProIntervall: 0,
      steuerJaehrlich: 0
    }
  }

  // Stichtag für neue Berechnungsmethode
  const stichtag = new Date(2020, 9, 1) // Monat ist 0-basiert, daher 9 für Oktober
  const istNachStichtag = erstzulassungsDatum >= stichtag

  // Grundsteuer berechnen (monatlich)
  let monatlicheSteuer = 0

  // Berechnung je nach Fahrzeugklasse und Antriebsart
  switch (fahrzeugKlasse) {
    case FahrzeugKlasse.L1E_BIS_L5E:
      monatlicheSteuer = berechneL1EBisL5E(antriebsart, istNachStichtag, motorLeistungKW, hubraumCCM, co2EmissionGKM)
      break
    case FahrzeugKlasse.M1_BIS_35T:
      monatlicheSteuer = berechneM1Bis35T(antriebsart, istNachStichtag, motorLeistungKW, co2EmissionGKM, eigengewichtKG, erstzulassungsDatum)
      break
    case FahrzeugKlasse.WOHNMOBIL_M1_SA:
      // Prüfen, ob der Parameter aufbauSA in den Params existiert
      const aufbauSA = (params as any).aufbauSA === true
      if (aufbauSA) {
        // Wohnmobil mit SA-Aufbau: Berechnung nach Punkt c) mit Obergrenze (wie andere Fahrzeuge)
        // Dies entspricht der Checkbox "Mit Aufbau SA" im UI, wenn sie aktiviert ist
        monatlicheSteuer = berechneAndereBis35T(antriebsart, istNachStichtag, motorLeistungKW)
      } else {
        // Wohnmobil ohne SA-Aufbau: Berechnung nach Punkt b) ohne Obergrenze (wie PKW)
        // Dies entspricht der Checkbox "Mit Aufbau SA" im UI, wenn sie deaktiviert ist
        monatlicheSteuer = berechneM1Bis35T(antriebsart, istNachStichtag, motorLeistungKW, co2EmissionGKM, eigengewichtKG, erstzulassungsDatum)
      }
      break
    case FahrzeugKlasse.LKW_N1_BIS_35T:
      monatlicheSteuer = berechneAndereBis35T(antriebsart, istNachStichtag, motorLeistungKW)
      break
    case FahrzeugKlasse.ANDERE_BIS_35T:
      monatlicheSteuer = berechneAndereBis35T(antriebsart, istNachStichtag, motorLeistungKW)
      break
  }

  // Zuschlag für Zahlungsintervall gemäß § 6 Abs. 3
  if (istZuschlagFuerZahlungsintervallAnwendbar(fahrzeugKlasse, antriebsart, istNachStichtag)) {
    monatlicheSteuer = monatlicheSteuer * getZahlungsintervallFaktor(zahlungsintervall)
  }

  // Zuschlag für alte Fremdzündungsmotoren gemäß § 6 Abs. 4
  if (istFremdzuendungsMotorVor1987 && !haeltSchadstoffgrenzwerteEin &&
      istZuschlagFuerAlteFremdzuendungsmotorenAnwendbar(fahrzeugKlasse, antriebsart, istNachStichtag)) {
    monatlicheSteuer = monatlicheSteuer * 1.2 // 20% Zuschlag
  }

  // Steuer für das angegebene Zahlungsintervall berechnen
  const monate = getMonateFuerZahlungsintervall(zahlungsintervall)
  const steuerProIntervall = monatlicheSteuer * monate

  return {
    monatlicheSteuer,
    steuerProIntervall,
    steuerJaehrlich: monatlicheSteuer * 12
  }
}

/**
 * Gibt die Anzahl der Monate für das angegebene Zahlungsintervall zurück.
 */
function getMonateFuerZahlungsintervall(zahlungsintervall: Zahlungsintervall): number {
  switch (zahlungsintervall) {
    case Zahlungsintervall.JAEHRLICH:
      return 12
    case Zahlungsintervall.HALBJAEHRLICH:
      return 6
    case Zahlungsintervall.VIERTELJAEHRLICH:
      return 3
    case Zahlungsintervall.MONATLICH:
    default:
      return 1
  }
}

function berechneL1EBisL5E(antriebsart: Antriebsart, istNachStichtag: boolean, 
                         motorLeistungKW: number, hubraumCCM?: number, co2EmissionGKM?: number): number {
  // § 6 Abs. 1 Z 1
  if (antriebsart === Antriebsart.ELEKTRISCH) {
    // Elektrischer Antrieb: 0,50 Euro je Kilowatt der um 5 Kilowatt verringerten Leistung
    const berechnungsLeistung = Math.max(motorLeistungKW - 5, 4)
    return 0.50 * berechnungsLeistung
  } else {
    if (!istNachStichtag) {
      // Vor 1. Oktober 2020: 0,025 Euro je Kubikzentimeter des Hubraums
      return 0.025 * (hubraumCCM || 0)
    } else {
      // Nach 30. September 2020: Kombination aus Hubraum und CO2-Emission
      const hubraumAnteil = 0.014 * Math.max((hubraumCCM || 0) - 52, 0)
      const co2Anteil = 0.20 * Math.max((co2EmissionGKM || 0) - 52, 10)
      return hubraumAnteil + co2Anteil
    }
  }
}

function berechneM1Bis35T(antriebsart: Antriebsart, istNachStichtag: boolean, 
                        motorLeistungKW: number, co2EmissionGKM?: number, 
                        eigengewichtKG?: number, erstzulassungsDatum?: Date): number {
  // § 6 Abs. 1 Z 2
  if (antriebsart === Antriebsart.ELEKTRISCH) {
    // Elektrischer Antrieb: Kombination aus Leistung und Eigengewicht
    const leistungsAnteil = berechneElektrischeLeistungM1(motorLeistungKW)
    const gewichtsAnteil = berechneElektrischesGewichtM1(eigengewichtKG || 0)
    return leistungsAnteil + gewichtsAnteil
  } else if (!istNachStichtag) {
    // Vor 1. Oktober 2020: Gestaffelte Berechnung nach Leistung
    return berechneVerbrennungsLeistungM1Alt(motorLeistungKW)
  } else {
    // Nach 30. September 2020: Je nach Antriebsart
    if (antriebsart === Antriebsart.HYBRID_EXTERN_AUFLADBAR) {
      return berechneHybridExternM1(motorLeistungKW, co2EmissionGKM || 0, erstzulassungsDatum || new Date())
    } else {
      return berechneHybridNichtExternOderVerbrennungM1(motorLeistungKW, co2EmissionGKM || 0, erstzulassungsDatum || new Date())
    }
  }
}

function berechneElektrischeLeistungM1(motorLeistungKW: number): number {
  // § 6 Abs. 1 Z 2 lit. a - für die Leistung
  const berechnungsLeistung = Math.max(motorLeistungKW - 45, 10)
  const leistungAnteil1 = Math.min(berechnungsLeistung, 35) * 0.25
  const leistungAnteil2 = Math.max(Math.min(berechnungsLeistung - 35, 25), 0) * 0.35
  const leistungAnteil3 = Math.max(berechnungsLeistung - 60, 0) * 0.45
  return leistungAnteil1 + leistungAnteil2 + leistungAnteil3
}

function berechneElektrischesGewichtM1(eigengewichtKG: number): number {
  // § 6 Abs. 1 Z 2 lit. a - für das Gewicht
  const berechnungsGewicht = Math.max(eigengewichtKG - 900, 200)
  const gewichtAnteil1 = Math.min(berechnungsGewicht, 500) * 0.015
  const gewichtAnteil2 = Math.max(Math.min(berechnungsGewicht - 500, 700), 0) * 0.030
  const gewichtAnteil3 = Math.max(berechnungsGewicht - 1200, 0) * 0.045
  return gewichtAnteil1 + gewichtAnteil2 + gewichtAnteil3
}

function berechneVerbrennungsLeistungM1Alt(motorLeistungKW: number): number {
  // § 6 Abs. 1 Z 2 lit. b
  const berechnungsLeistung = Math.max(motorLeistungKW - 24, 0)
  const leistungAnteil1 = Math.min(berechnungsLeistung, 66) * 0.62
  const leistungAnteil2 = Math.max(Math.min(berechnungsLeistung - 66, 20), 0) * 0.66
  const leistungAnteil3 = Math.max(berechnungsLeistung - 86, 0) * 0.75
  return Math.max(leistungAnteil1 + leistungAnteil2 + leistungAnteil3, 6.20)
}

function berechneHybridExternM1(motorLeistungKW: number, co2EmissionGKM: number, erstzulassungsDatum: Date): number {
  // § 6 Abs. 1 Z 2 lit. c sublit. aa
  const zulassungsJahr = erstzulassungsDatum.getFullYear()
  const abzugsbetragLeistung = getAbzugsbetragLeistung(zulassungsJahr)
  const abzugsbetragCO2Gewichtet = getAbzugsbetragCO2Gewichtet(zulassungsJahr)
  
  const berechnungsLeistung = Math.max(motorLeistungKW - abzugsbetragLeistung, 5)
  const berechnungsCO2 = Math.max(co2EmissionGKM - abzugsbetragCO2Gewichtet, 5)
  
  return 0.72 * berechnungsLeistung + 0.72 * berechnungsCO2
}

function berechneHybridNichtExternOderVerbrennungM1(motorLeistungKW: number, co2EmissionGKM: number, erstzulassungsDatum: Date): number {
  // § 6 Abs. 1 Z 2 lit. c sublit. bb
  const zulassungsJahr = erstzulassungsDatum.getFullYear()
  const abzugsbetragLeistung = getAbzugsbetragLeistung(zulassungsJahr)
  const abzugsbetragCO2 = getAbzugsbetragCO2(zulassungsJahr)
  
  const berechnungsLeistung = Math.max(motorLeistungKW - abzugsbetragLeistung, 5)
  const berechnungsCO2 = Math.max(co2EmissionGKM - abzugsbetragCO2, 5)
  
  return 0.72 * berechnungsLeistung + 0.72 * berechnungsCO2
}


function berechneAndereBis35T(antriebsart: Antriebsart, istNachStichtag: boolean, motorLeistungKW: number): number {
  // § 6 Abs. 1 Z 3
  if (antriebsart === Antriebsart.ELEKTRISCH) {
    return berechneElektrischeLeistungAndere(motorLeistungKW)
  } else if (!istNachStichtag) {
    return berechneVerbrennungsLeistungAndereAlt(motorLeistungKW)
  } else {
    return berechneVerbrennungsLeistungAndereNeu(motorLeistungKW)
  }
}

function berechneElektrischeLeistungAndere(motorLeistungKW: number): number {
  // § 6 Abs. 1 Z 3 lit. a
  const berechnungsLeistung = Math.max(motorLeistungKW - 30, 10)
  const leistungAnteil1 = Math.min(berechnungsLeistung, 70) * 0.40
  const leistungAnteil2 = Math.max(berechnungsLeistung - 70, 0) * 0.50
  return leistungAnteil1 + leistungAnteil2
}

function berechneVerbrennungsLeistungWohnmobilAlt(motorLeistungKW: number): number {
  // § 6 Abs. 1 Z 3 lit. b
  const berechnungsLeistung = Math.max(motorLeistungKW - 24, 0)
  const leistungAnteil1 = Math.min(berechnungsLeistung, 66) * 0.62
  const leistungAnteil2 = Math.max(Math.min(berechnungsLeistung - 66, 20), 0) * 0.66
  const leistungAnteil3 = Math.max(berechnungsLeistung - 86, 0) * 0.75
  return Math.min(Math.max(leistungAnteil1 + leistungAnteil2 + leistungAnteil3, 6.20), 72.0)
}

function berechneVerbrennungsLeistungAndereAlt(motorLeistungKW: number): number {
  // § 6 Abs. 1 Z 3 lit. b
  const berechnungsLeistung = Math.max(motorLeistungKW - 24, 0)
  const leistungAnteil1 = Math.min(berechnungsLeistung, 66) * 0.62
  const leistungAnteil2 = Math.max(Math.min(berechnungsLeistung - 66, 20), 0) * 0.66
  const leistungAnteil3 = Math.max(berechnungsLeistung - 86, 0) * 0.75
  return Math.min(Math.max(leistungAnteil1 + leistungAnteil2 + leistungAnteil3, 6.20), 72.0)
}

function berechneVerbrennungsLeistungAndereNeu(motorLeistungKW: number): number {
  // § 6 Abs. 1 Z 3 lit. c
  const berechnungsLeistung = Math.max(motorLeistungKW - 24, 0)
  const leistungAnteil1 = Math.min(berechnungsLeistung, 66) * 0.65
  const leistungAnteil2 = Math.max(Math.min(berechnungsLeistung - 66, 20), 0) * 0.70
  const leistungAnteil3 = Math.max(berechnungsLeistung - 86, 0) * 0.79
  return Math.min(Math.max(leistungAnteil1 + leistungAnteil2 + leistungAnteil3, 6.50), 76.0)
}

function getZahlungsintervallFaktor(zahlungsintervall: Zahlungsintervall): number {
  // Gemäß § 6 Abs. 3 Z 2
  switch (zahlungsintervall) {
    case Zahlungsintervall.HALBJAEHRLICH:
      return 1.06 // +6%
    case Zahlungsintervall.VIERTELJAEHRLICH:
      return 1.08 // +8%
    case Zahlungsintervall.MONATLICH:
      return 1.10 // +10%
    case Zahlungsintervall.JAEHRLICH:
    default:
      return 1.0 // kein Zuschlag
  }
}

function istZuschlagFuerZahlungsintervallAnwendbar(
  fahrzeugKlasse: FahrzeugKlasse, 
  antriebsart: Antriebsart, 
  istNachStichtag: boolean
): boolean {
  // Für alle Fahrzeuge mit Erstzulassung nach dem 30. September 2020 
  // entfallen die Zuschläge für unterjährige Zahlungen komplett
  if (istNachStichtag) {
    return false
  }
  
  // Bei Fahrzeugen vor dem Stichtag:
  // Zuschlag für Zahlungsintervall gilt nicht für Elektrofahrzeuge
  if (antriebsart === Antriebsart.ELEKTRISCH) {
    return false
  }
  
  return true
}

function istZuschlagFuerAlteFremdzuendungsmotorenAnwendbar(
  fahrzeugKlasse: FahrzeugKlasse, 
  antriebsart: Antriebsart, 
  istNachStichtag: boolean
): boolean {
  // Zuschlag für alte Fremdzündungsmotoren gilt nicht für:
  // 1. Elektrofahrzeuge
  if (antriebsart === Antriebsart.ELEKTRISCH) {
    return false
  }
  
  // 2. Hybridfahrzeuge nach Stichtag
  if (istNachStichtag && 
     (antriebsart === Antriebsart.HYBRID_EXTERN_AUFLADBAR || 
      antriebsart === Antriebsart.HYBRID_NICHT_EXTERN_AUFLADBAR)) {
    return false
  }
  
  return true
}

function getAbzugsbetragLeistung(jahr: number): number {
  // § 6 Abs. 1 Z 2 lit. c
  // cc) Der Abzugsbetrag von der Leistung des Verbrennungsmotors in Kilowatt
  if (jahr >= 2033) return Math.max(53 - (jahr - 2033), 0)
  if (jahr === 2032) return 53
  if (jahr === 2031) return 54
  if (jahr === 2030) return 55
  if (jahr === 2029) return 56
  if (jahr === 2028) return 57
  if (jahr === 2027) return 58
  if (jahr === 2026) return 59
  if (jahr === 2025) return 60
  if (jahr === 2024) return 61
  if (jahr === 2023) return 62
  if (jahr === 2022) return 63
  if (jahr === 2021) return 64
  if (jahr === 2020) return 65 // Anfangswert für 2020
  return 0 // Vor 2020
}

function getAbzugsbetragCO2(jahr: number): number {
  // § 6 Abs. 1 Z 2 lit. c
  // cc) Der Abzugsbetrag von den kombinierten CO₂-Emissionen in g/km
  if (jahr >= 2033) return Math.max(79 - ((jahr - 2033) * 3), 0)
  if (jahr === 2032) return 79
  if (jahr === 2031) return 82
  if (jahr === 2030) return 85
  if (jahr === 2029) return 88
  if (jahr === 2028) return 91
  if (jahr === 2027) return 94
  if (jahr === 2026) return 97
  if (jahr === 2025) return 100
  if (jahr === 2024) return 103
  if (jahr === 2023) return 106
  if (jahr === 2022) return 109
  if (jahr === 2021) return 112
  if (jahr === 2020) return 115 // Anfangswert für 2020
  return 0 // Vor 2020
}

function getAbzugsbetragCO2Gewichtet(jahr: number): number {
  // § 6 Abs. 1 Z 2 lit. c
  // cc) Der Abzugsbetrag von den gewichtet kombinierten CO₂-Emissionen in g/km für Plugin-Hybride
  if (jahr >= 2033) return Math.max(32 - (jahr - 2033), 0)
  if (jahr === 2032) return 32
  if (jahr === 2031) return 33
  if (jahr === 2030) return 34
  if (jahr === 2029) return 35
  if (jahr === 2028) return 36
  if (jahr === 2027) return 24
  if (jahr === 2026) return 24
  if (jahr === 2025) return 12
  if (jahr === 2024) return 13
  if (jahr === 2023) return 14
  if (jahr === 2022) return 15
  if (jahr === 2021) return 16
  if (jahr === 2020) return 17 // Anfangswert für 2020
  return 0 // Vor 2020
}
